// Jax Counter Strike stance VFX — a fast-spinning lamp post around Jax that
// reads as "deflecting everything coming at me". Lifetime is tied to the
// stance itself: the spin keeps going as long as `isActive()` returns true,
// then fades out.

import type { Animation } from "../../engine/animation/AnimationQueue";
import type { Point } from "../../engine/hex/HexLayout";

const FADE_IN_MS = 180;
const FADE_OUT_MS = 260;
const SPIN_RATE = Math.PI * 6; // radians per second — fast enough to blur

export class JaxCounterStrikeVFX implements Animation {
  private elapsed = 0;
  private fadeOutStart: number | null = null;

  constructor(
    private getCenter: () => Point,
    private radius: number,
    private isActive: () => boolean,
  ) {}

  update(dt: number): void {
    this.elapsed += dt;
    if (this.fadeOutStart === null && !this.isActive()) {
      this.fadeOutStart = this.elapsed;
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const fadeIn = Math.min(this.elapsed / FADE_IN_MS, 1);
    const fadeOut = this.fadeOutStart === null
      ? 1
      : Math.max(0, 1 - (this.elapsed - this.fadeOutStart) / FADE_OUT_MS);
    const fade = fadeIn * fadeOut;
    if (fade <= 0) return;

    const center = this.getCenter();
    const angle = (this.elapsed / 1000) * SPIN_RATE;
    const pulseT = this.elapsed / 1000;

    ctx.save();
    ctx.translate(center.x, center.y);

    this.drawDodgeAura(ctx, fade, pulseT);
    this.drawSpinningStaff(ctx, angle, fade);
    this.drawSpinningStaff(ctx, angle + Math.PI, fade);
    this.drawDodgeRing(ctx, fade, pulseT);

    ctx.restore();
  }

  // Soft purple disc behind Jax to ground the spin.
  private drawDodgeAura(ctx: CanvasRenderingContext2D, fade: number, t: number): void {
    const pulse = 0.5 + 0.5 * Math.sin(t * Math.PI * 6);
    const r = this.radius * 0.95;
    const grad = ctx.createRadialGradient(0, 0, r * 0.2, 0, 0, r);
    grad.addColorStop(0, `rgba(168, 85, 247, ${(0.32 * fade).toFixed(3)})`);
    grad.addColorStop(0.6, `rgba(124, 58, 237, ${(0.18 * fade).toFixed(3)})`);
    grad.addColorStop(1, `rgba(124, 58, 237, 0)`);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(0, 0, r * (0.92 + 0.06 * pulse), 0, Math.PI * 2);
    ctx.fill();
  }

  // One end of the spinning lamp post — a thick bar with motion-blur trail.
  private drawSpinningStaff(ctx: CanvasRenderingContext2D, angle: number, fade: number): void {
    const r = this.radius * 0.95;
    const TRAIL_STEPS = 8;
    const TRAIL_ARC = Math.PI * 0.55;

    for (let i = TRAIL_STEPS; i >= 1; i--) {
      const frac = i / TRAIL_STEPS;
      const a = angle - TRAIL_ARC * frac;
      const alpha = fade * (1 - frac) * 0.45;
      const width = 5 * (1 - frac * 0.5);
      ctx.save();
      ctx.rotate(a);
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = "#c084fc";
      ctx.lineCap = "round";
      ctx.lineWidth = width;
      ctx.shadowColor = "#a855f7";
      ctx.shadowBlur = 10;
      ctx.beginPath();
      ctx.moveTo(r * 0.18, 0);
      ctx.lineTo(r * 0.95, 0);
      ctx.stroke();
      ctx.restore();
    }

    ctx.save();
    ctx.rotate(angle);
    ctx.globalAlpha = fade;

    ctx.shadowColor = "#a855f7";
    ctx.shadowBlur = 18;
    ctx.strokeStyle = "#e9d5ff";
    ctx.lineCap = "round";
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.moveTo(r * 0.18, 0);
    ctx.lineTo(r * 0.95, 0);
    ctx.stroke();

    ctx.shadowBlur = 14;
    ctx.fillStyle = "#fef3c7";
    ctx.beginPath();
    ctx.arc(r * 0.95, 0, 4.5, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  // Faint outline to communicate "untouchable" — the dodge bubble.
  private drawDodgeRing(ctx: CanvasRenderingContext2D, fade: number, t: number): void {
    const r = this.radius * (1.02 + 0.04 * Math.sin(t * Math.PI * 8));
    ctx.globalAlpha = fade * 0.55;
    ctx.strokeStyle = "#a855f7";
    ctx.lineWidth = 2;
    ctx.shadowColor = "#7c3aed";
    ctx.shadowBlur = 12;
    ctx.setLineDash([8, 6]);
    ctx.lineDashOffset = -t * 60;
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  isDone(): boolean {
    return this.fadeOutStart !== null && (this.elapsed - this.fadeOutStart) >= FADE_OUT_MS;
  }
}
